import { useState, useEffect } from 'react';
import { getCurrentUser } from '@aws-amplify/auth';
import { hasActiveSubscription } from './subscription';
import { applyThemeBasedOnAuth } from './theme';

export interface AuthState {
  progress: 'CHECKING_USER' | 'CHECKING_SUBSCRIPTION' | 'DONE';
  userStatus: 'CHECKING' | 'ANONYMOUS' | 'FREE' | 'PRO';
}

export interface UseAuthStateReturn {
  authState: AuthState;
  hasSubscription: boolean;
  isSignedIn: boolean;
  isLoading: boolean;
}

export const useAuthState = (): UseAuthStateReturn => {
  const [authState, setAuthState] = useState<AuthState>({
    progress: 'CHECKING_USER',
    userStatus: 'CHECKING'
  });

  // Initialize theme and check auth/subscription status
  useEffect(() => {
    const initializeAuthAndTheme = async () => {
      // Initialize theme with default values
      applyThemeBasedOnAuth(false, false);
      
      try {
        // Check if user is signed in
        await getCurrentUser();
        
        // User is signed in, now check subscription
        setAuthState({
          progress: 'CHECKING_SUBSCRIPTION',
          userStatus: 'CHECKING'
        });
        
        try {
          const isActive = await hasActiveSubscription();
          setAuthState({
            progress: 'DONE',
            userStatus: isActive ? 'PRO' : 'FREE'
          });
        } catch (subscriptionError) {
          console.error('Error checking subscription:', subscriptionError);
          setAuthState({
            progress: 'DONE',
            userStatus: 'FREE'
          });
        }
      } catch (error) {
        // User is not signed in
        setAuthState({
          progress: 'DONE',
          userStatus: 'ANONYMOUS'
        });
      }
    };
    
    initializeAuthAndTheme();
  }, []);

  // Apply final theme when auth state is complete
  useEffect(() => {
    if (authState.progress === 'DONE') {
      const isAuthenticated = authState.userStatus !== 'ANONYMOUS';
      const hasSubscription = authState.userStatus === 'PRO';
      applyThemeBasedOnAuth(isAuthenticated, hasSubscription);
    }
  }, [authState]);

  // Helper functions to derive values from authState
  const hasSubscription = authState.userStatus === 'PRO';
  const isSignedIn = authState.userStatus !== 'ANONYMOUS';
  const isLoading = authState.progress !== 'DONE';

  return {
    authState,
    hasSubscription,
    isSignedIn,
    isLoading
  };
}; 